"use client";

import { useEffect, useState } from "react";
import { NotebookPen, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { createClient } from "@/lib/supabase/client";
import { AccountGateButton } from "@/components/auth/account-gate-button";

interface NoteRow {
  note: string;
}

// Defensive Obergrenze (kein Zod/API-Layer hier - direkter Browser-Client-Upsert, siehe
// XSS-/Input-Audit STATUS.md 2026-08-06). Rendering selbst ist bereits sicher
// (kontrollierte <Textarea value>, kein dangerouslySetInnerHTML) - das hier verhindert
// nur unbegrenzt große Zeilen in der DB.
const NOTE_MAX_LENGTH = 2000;

/**
 * Private Notiz/These je Aktie (siehe backlog.md -> "Feedback-Runde 4" Punkt 6).
 * Nutzt direkt den RLS-geschuetzten Browser-Client (gleiches Muster wie
 * WatchlistStarButton/ScoreAlertButton) statt einer eigenen API-Route. Nur fuer
 * eingeloggte Nutzer sichtbar - eine private Notiz ohne Konto ergibt keinen Sinn.
 *
 * **Tag-Funktion entfernt (backlog.md -> [Punkt 24])**: Erik empfand den Nutzen der Tags
 * als unklar - Feld ersatzlos gestrichen, stattdessen ein erklaerender Hinweistext direkt
 * am Notizfeld, was das Eintragen einer Notiz bringt. `user_notes.tags` (DB-Spalte, `text[]
 * not null default '{}'`) bleibt unangetastet (bereits gespeicherte Tags gehen nicht
 * verloren, neue Upserts lassen die Spalte einfach unangefasst) - kein Migrations-Bedarf.
 */
export function NoteButton({ symbol }: { symbol: string }) {
  const [userId, setUserId] = useState<string | null | undefined>(undefined);
  const [hasNote, setHasNote] = useState(false);
  const [note, setNote] = useState("");
  const [isPending, setIsPending] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    let cancelled = false;

    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) setUserId(data.user?.id ?? null);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const supabase = createClient();
    supabase
      .from("user_notes")
      .select("note")
      .eq("symbol", symbol)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const row = data as NoteRow | null;
        if (row) {
          setHasNote(true);
          setNote(row.note);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [userId, symbol]);

  // Auth-Status noch nicht geladen: noch nichts rendern.
  if (userId === undefined) return null;

  // Gast (Erik 2026-07-17: private Notizen verlangen ein kostenloses Konto) - Button
  // bleibt sichtbar, oeffnet aber das Signup-Gate statt der Notiz-Einrichtung.
  if (userId === null) {
    return (
      <AccountGateButton
        label="Anmelden, um eine Notiz hinzuzufügen"
        reason={`Erstelle ein kostenloses Konto, um eine private Notiz zu ${symbol} zu speichern.`}
      >
        <Lock className="size-4" />
      </AccountGateButton>
    );
  }

  async function handleSave() {
    const trimmedNote = note.trim().slice(0, NOTE_MAX_LENGTH);
    if (trimmedNote.length === 0) return;

    setIsPending(true);
    const supabase = createClient();
    const { error } = await supabase.from("user_notes").upsert(
      {
        user_id: userId,
        symbol,
        note: trimmedNote,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id,symbol" },
    );
    if (!error) {
      setHasNote(true);
      setOpen(false);
    }
    setIsPending(false);
  }

  async function handleRemove() {
    setIsPending(true);
    const supabase = createClient();
    const { error } = await supabase.from("user_notes").delete().eq("symbol", symbol).eq("user_id", userId);
    if (!error) {
      setHasNote(false);
      setNote("");
      setOpen(false);
    }
    setIsPending(false);
  }

  const label = hasNote ? "Notiz bearbeiten" : "Notiz hinzufügen";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="icon" aria-label={label} title={label}>
          <NotebookPen className={hasNote ? "size-4 text-gold-text" : "size-4"} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <p className="text-xs text-muted-foreground">
          Halte fest, warum du {symbol} beobachtest oder worauf du achten willst - hilft dir,
          deine eigene Einschätzung später nachzuvollziehen. Nur für dich sichtbar.
        </p>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Eigene Investment-These, Beobachtungen, Erinnerungen…"
          aria-label="Notiz"
          rows={4}
          maxLength={NOTE_MAX_LENGTH}
          className="bg-card"
        />
        <div className="flex gap-2">
          <Button type="button" size="sm" onClick={handleSave} disabled={isPending}>
            Speichern
          </Button>
          {hasNote && (
            <Button type="button" size="sm" variant="ghost" onClick={handleRemove} disabled={isPending}>
              Entfernen
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
